import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { API_CONFIG } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';
import { toast } from '../../../utils/simpleToast';
import { 
  MessageSquare, 
  Search, 
  Trash2, 
  Eye, 
  Calendar,
  User,
  Bot,
  Clock,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Send,
  UserCog
} from 'lucide-react';

// React Toastify kullanılıyor (bildirimler için)

const AdminGogiConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [isTakenOver, setIsTakenOver] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const typingTimeoutRef = useRef(null);
  const notifiedMessageIdsRef = useRef(new Set()); // Bildirim gönderilen mesaj ID'lerini takip et (veritabanı bildirimleri için)
  const notificationSoundRef = useRef(null); // Bildirim sesi için audio element

  useEffect(() => {
    loadConversations();
    
    // URL parametresinden session_id'yi oku ve konuşmayı aç
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = urlParams.get('session_id');
    if (sessionIdFromUrl) {
      loadConversationDetail(sessionIdFromUrl);
      setShowDetail(true);
      // URL'den session_id parametresini temizle
      window.history.replaceState({}, '', '/admin/gogiai-conversations');
    }
  }, [currentPage]);

  // Favicon'u gogi.svg olarak ayarla (sadece bu sayfada)
  useEffect(() => {
    // Mevcut favicon linklerini kaldır
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());
    
    // Yeni favicon linkini oluştur
    const link = document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'icon';
    link.href = '/gogi.svg';
    document.getElementsByTagName('head')[0].appendChild(link);
    
    return () => {
      // Cleanup: sayfa değiştiğinde favicon'u kaldır (diğer sayfalarda favicon olmasın)
      const links = document.querySelectorAll("link[rel*='icon']");
      links.forEach(link => link.remove());
    };
  }, []);

  // Service Worker mesajlarını dinle (sadece ses çalma için)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
          playNotificationSound();
        }
      };
      
      navigator.serviceWorker.addEventListener('message', handleMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  // Yeni mesaj kontrolü
  useEffect(() => {
    // İlk yüklemede konuşmaları yükle
    loadConversations();
    
    // Test: Toast sisteminin çalıştığını kontrol et
    setTimeout(() => {
      toast.success('Bildirim sistemi aktif!', {
        duration: 3000,
      });
    }, 2000);

    // Yeni mesaj kontrolü için polling (her 10 saniyede bir)
    const checkNewMessages = setInterval(() => {
      // Önce bildirimleri kontrol et (veritabanından)
      loadNotifications();
      // Sonra konuşmaları yükle
      loadConversations();
    }, 10000);

    return () => clearInterval(checkNewMessages);
  }, [currentPage]);

  // Sayfa görünürlüğü değiştiğinde kontrol et
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Sayfa görünür hale geldiğinde yeni mesajları kontrol et
      if (document.visibilityState === 'visible') {
        // Kısa bir gecikme ile kontrol et (sayfa tam yüklensin)
        setTimeout(() => {
          loadNotifications();
          loadConversations();
        }, 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentPage]);

  // Bildirim sesi çalma fonksiyonu
  const playNotificationSound = () => {
    try {
      // Audio element oluştur veya mevcut olanı kullan
      if (!notificationSoundRef.current) {
        // not.mp3 dosyasını kullan (ana dizinde)
        notificationSoundRef.current = new Audio('/not.mp3');
        notificationSoundRef.current.volume = 0.7; // Ses seviyesi
        
        // Ses dosyası yüklenme hatası kontrolü
        notificationSoundRef.current.addEventListener('error', (e) => {
          playFallbackSound();
        });
      }
      
      // Sesi sıfırla ve çal
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch((error) => {
        // Ses dosyası yoksa veya çalınamazsa, Web Audio API ile basit bir bip sesi oluştur
        playFallbackSound();
      });
    } catch (error) {
      console.error('[GogiAI Notifications] Sound error:', error);
      playFallbackSound();
    }
  };

  // Fallback ses (Web Audio API ile) - Basit bip sesi
  const playFallbackSound = () => {
    try {
      // AudioContext oluştur (kullanıcı etkileşimi gerektirebilir)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Hoş bir bildirim sesi (800 Hz, kısa bip)
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      // Fade out efekti
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // AudioContext hatası (kullanıcı etkileşimi gerekebilir veya tarayıcı desteklemiyor)
      // Ses çalma hatası normal, görsel bildirim yeterli
    }
  };

  // Sayfa görünür olduğunda bildirim listesini temizle (kullanıcı sayfayı görüyorsa bildirim gerekmez)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Sayfa görünür olduğunda, bildirim listesini temizle
        // Böylece kullanıcı sayfayı görüyorsa yeni bildirimler gösterilir
        // NOT: notifiedMessageIdsRef'ı temizlemiyoruz çünkü her mesaj için sadece bir kez bildirim göndermek istiyoruz
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Bildirimleri yükle (veritabanından)
  const loadNotifications = async () => {
    try {
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getNotifications&limit=10&unread_only=true`);
      const data = await response.json();
      
      if (data.status === 'success' && data.data && data.data.length > 0) {
        // Her bildirim için toast göster
        for (const notification of data.data) {
          // Eğer bu bildirim için daha önce toast gösterilmediyse
          const notificationKey = `${notification.type}-${notification.reference_id || 'none'}`;
          if (!notifiedMessageIdsRef.current.has(notificationKey)) {
            const notificationUrl = notification.parameters?.session_id 
              ? `/admin/gogiai-conversations?session_id=${notification.parameters.session_id}`
              : '/admin/gogiai-conversations';
            
            // Toast göster
            toast.info(notification.body, {
              title: notification.title,
              duration: 8000,
              onClick: () => {
                window.location.href = notificationUrl;
              },
            });
            
            // Ses çal
            playNotificationSound();
            
            // Bildirim gösterildiğini işaretle
            notifiedMessageIdsRef.current.add(notificationKey);
          }
        }
      }
    } catch (error) {
      console.error('[GogiAI Notifications] Error loading notifications:', error);
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getGogiaiConversations&page=${currentPage}&limit=20`);
      const data = await response.json();
      
      if (data.status === 'success') {
        // Konuşmaları güncelle
        setConversations(data.data);
        setTotalPages(data.pagination.pages);
      }
    } catch (error) {
      // Konuşmalar yükleme hatası
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetail = async (sessionId) => {
    try {
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getGogiaiConversationDetail&session_id=${sessionId}`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setSelectedConversation(data.data);
        setSelectedSessionId(sessionId);
        setShowDetail(true);
        // Konuşmanın devralınıp devralınmadığını kontrol et
        setIsTakenOver(data.is_taken_over || false);
        // Konuşmanın tamamlanıp tamamlanmadığını kontrol et
        setIsEnded(data.is_ended || false);
      }
    } catch (error) {
      // Konuşma detayı yükleme hatası
    }
  };

  const deleteConversation = async (sessionId) => {
    if (!confirm('Bu konuşmayı ve ilgili müşteri bilgilerini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
    
    try {
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=deleteGogiaiConversation`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        loadConversations();
        setShowDetail(false);
        setSelectedConversation(null);
        setSelectedSessionId(null);
        alert('Konuşma başarıyla silindi.');
      } else {
        alert('Konuşma silinirken hata oluştu: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      alert('Konuşma silinirken hata oluştu: ' + error.message);
    }
  };

  const takeOverConversation = async (sessionId) => {
    if (!confirm('Bu konuşmayı devralmak istediğinizden emin misiniz? Artık yapay zeka cevap vermeyecek, siz manuel olarak cevap vereceksiniz.')) return;
    
    try {
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=takeOverConversation`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setIsTakenOver(true);
        // Konuşma detayını yeniden yükle (devralındı mesajını görmek için)
        await loadConversationDetail(sessionId);
        alert('Konuşma başarıyla devralındı. Artık manuel olarak mesaj gönderebilirsiniz.');
      } else {
        alert('Konuşma devralınırken hata oluştu: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      alert('Konuşma devralınırken hata oluştu: ' + error.message);
    }
  };

  const sendManualMessage = async (sessionId) => {
    if (!manualMessage.trim()) {
      alert('Lütfen bir mesaj yazın.');
      return;
    }
    
    // Typing timeout'unu temizle ve typing durumunu kapat
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setAdminTypingStatus(false);
    
    setSendingMessage(true);
    try {
      const response = await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=sendManualMessage`, {
        method: 'POST',
        body: JSON.stringify({ 
          session_id: sessionId,
          message: manualMessage.trim()
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setManualMessage('');
        // Konuşma detayını yeniden yükle
        await loadConversationDetail(sessionId);
        alert('Mesaj başarıyla gönderildi.');
      } else {
        alert('Mesaj gönderilirken hata oluştu: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      alert('Mesaj gönderilirken hata oluştu: ' + error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const setAdminTypingStatus = async (isTyping) => {
    if (!selectedSessionId) return;
    
    // Önceki timeout'u temizle
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    // Eğer typing durumu açılıyorsa, hemen gönder
    if (isTyping) {
      try {
        await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=setAdminTyping`, {
          method: 'POST',
          body: JSON.stringify({
            session_id: selectedSessionId,
            is_typing: true
          })
        });
      } catch (error) {
        // Set typing status error
      }
    } else {
      // Eğer typing durumu kapanıyorsa, hemen kapat (textarea boşsa)
      try {
        await adminApiFetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=setAdminTyping`, {
          method: 'POST',
          body: JSON.stringify({
            session_id: selectedSessionId,
            is_typing: false
          })
        });
      } catch (error) {
        // Set typing status error
      }
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const filteredConversations = conversations.filter(conv => 
    (conv.user_messages && conv.user_messages.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (conv.bot_messages && conv.bot_messages.toLowerCase().includes(searchTerm.toLowerCase())) ||
    conv.session_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showDetail && selectedConversation) {
    return (
      <div className="space-y-4 md:space-y-6 px-2 md:px-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
          <div className="flex items-center space-x-2 md:space-x-4">
            <Button
              variant="outline"
              onClick={() => setShowDetail(false)}
              className="flex items-center space-x-2"
              size="sm"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Geri</span>
            </Button>
            <h2 className="text-lg md:text-2xl font-bold">Konuşma Detayı</h2>
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            {!isTakenOver && (
              <Button
                variant="outline"
                onClick={() => takeOverConversation(selectedSessionId)}
                className="flex items-center space-x-2 bg-orange-50 hover:bg-orange-100 text-orange-600 border-orange-300 text-xs md:text-sm"
                size="sm"
              >
                <UserCog className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Konuşmayı Devral</span>
                <span className="sm:hidden">Devral</span>
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => deleteConversation(selectedSessionId)}
              className="flex items-center space-x-2 text-xs md:text-sm"
              size="sm"
            >
              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Konuşmayı Sil</span>
              <span className="sm:hidden">Sil</span>
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-3 md:p-6">
            <div className="space-y-4 md:space-y-6">
              {selectedConversation.map((message, index) => (
                <div key={index} className="space-y-3 md:space-y-4">
                  {/* Kullanıcı Mesajı */}
                  {message.user_message && (
                    <div className="flex space-x-2 md:space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-500 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 space-y-1 md:space-y-2 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 gap-1">
                          <span className="font-semibold text-blue-600 text-sm md:text-base">
                            {message.full_name && message.full_name.trim() !== '' 
                              ? message.full_name 
                              : 'Kullanıcı'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                          <p className="text-xs md:text-sm whitespace-pre-wrap text-gray-800 break-words">
                            {message.user_message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Gogi Yanıtı */}
                  {message.bot_response && (
                    <div className="flex space-x-2 md:space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-500 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 space-y-1 md:space-y-2 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 gap-1">
                          <span className="font-semibold text-purple-600 text-sm md:text-base">Gogi AI</span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 md:p-4">
                          <p className="text-xs md:text-sm whitespace-pre-wrap text-gray-800 break-words">
                            {message.bot_response}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Manuel Mesaj Gönderme (Konuşma Devralındıysa ve Tamamlanmamışsa) */}
        {isTakenOver && !isEnded && (
          <Card>
            <CardHeader className="p-3 md:p-6">
              <CardTitle className="flex items-center space-x-2 text-base md:text-lg">
                <UserCog className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                <span>Manuel Mesaj Gönder</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 md:space-y-4 p-3 md:p-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 md:p-3">
                <p className="text-xs md:text-sm text-orange-800">
                  ⚠️ Bu konuşma devralındı. Yapay zeka artık cevap vermeyecek. Mesajlarınız direkt olarak kullanıcıya gönderilecek.
                </p>
              </div>
              <textarea
                value={manualMessage}
                onChange={(e) => {
                  setManualMessage(e.target.value);
                  // Textarea'da karakter varsa typing durumunu aç, yoksa kapat
                  if (e.target.value.trim().length > 0) {
                    setAdminTypingStatus(true);
                  } else {
                    setAdminTypingStatus(false);
                  }
                }}
                onBlur={() => {
                  // Textarea'dan çıkınca: Eğer boşsa typing durumunu kapat, değilse açık tut
                  if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                    typingTimeoutRef.current = null;
                  }
                  // Textarea boşsa typing durumunu kapat
                  if (!manualMessage.trim()) {
                    setAdminTypingStatus(false);
                  }
                  // Textarea'da karakter varsa typing durumu açık kalır (zaten açık)
                }}
                placeholder="Kullanıcıya göndermek istediğiniz mesajı yazın..."
                className="w-full h-24 md:h-32 p-3 md:p-4 border border-gray-300 rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    sendManualMessage(selectedSessionId);
                  }
                }}
              />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-xs text-gray-500">
                  Ctrl + Enter ile gönderebilirsiniz
                </p>
                <Button
                  onClick={() => sendManualMessage(selectedSessionId)}
                  disabled={sendingMessage || !manualMessage.trim()}
                  className="flex items-center justify-center space-x-2 bg-orange-600 hover:bg-orange-700 w-full sm:w-auto text-xs md:text-sm"
                  size="sm"
                >
                  {sendingMessage ? (
                    <>
                      <RefreshCw className="w-3 h-3 md:w-4 md:h-4 animate-spin" />
                      <span>Gönderiliyor...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3 h-3 md:w-4 md:h-4" />
                      <span>Mesaj Gönder</span>
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-0">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">GogiAI Konuşmaları</h2>
          <p className="text-sm md:text-base text-gray-600">Müşteri konuşmalarını görüntüleyin ve yönetin</p>
        </div>
        <Button onClick={loadConversations} className="flex items-center space-x-2 w-full sm:w-auto" size="sm">
          <RefreshCw className="w-4 h-4" />
          <span>Yenile</span>
        </Button>
      </div>

      <Card>
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-lg md:text-xl">Konuşma Listesi</CardTitle>
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Konuşma ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64 text-sm md:text-base"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin" />
              <span className="ml-2 text-sm md:text-base">Yükleniyor...</span>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {filteredConversations.map((conversation) => {
                // Cinsiyet emoji'si
                const genderEmoji = conversation.gender === 'male' ? '👨' : 
                                  conversation.gender === 'female' ? '👩' : '👤';
                
                return (
                  <div
                    key={conversation.session_id}
                    className="border rounded-lg p-3 md:p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-lg md:text-xl">{genderEmoji}</span>
                          <span className="font-medium text-gray-700 text-sm md:text-base truncate">
                            {conversation.full_name && conversation.full_name.trim() !== '' 
                              ? conversation.full_name 
                              : 'İsimsiz Kullanıcı'}
                          </span>
                          <Badge variant="outline" className="flex items-center space-x-1 text-xs">
                            <MessageSquare className="w-3 h-3" />
                            <span>{conversation.message_count} mesaj</span>
                          </Badge>
                          {conversation.ended_at ? (
                            <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs">
                              Tamamlandı
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-100 text-green-600 text-xs">
                              Aktif
                            </Badge>
                          )}
                          <span className="text-xs text-gray-500 hidden sm:inline">
                            {formatDate(conversation.last_message)}
                          </span>
                        </div>
                        <div className="space-y-1.5 md:space-y-2 mb-2">
                          {conversation.user_messages && (
                            <div className="flex items-start space-x-2">
                              <User className="w-3 h-3 md:w-4 md:h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <p className="text-xs md:text-sm text-gray-700 break-words">
                                <span className="font-medium text-blue-600">
                                  {conversation.full_name && conversation.full_name.trim() !== '' 
                                    ? conversation.full_name 
                                    : 'Kullanıcı'}:
                                </span>{' '}
                                {conversation.user_messages.length > 100 
                                  ? conversation.user_messages.substring(0, 100) + '...'
                                  : conversation.user_messages
                                }
                              </p>
                            </div>
                          )}
                          {conversation.bot_messages && (
                            <div className="flex items-start space-x-2">
                              <Bot className="w-3 h-3 md:w-4 md:h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                              <p className="text-xs md:text-sm text-gray-700 break-words">
                                <span className="font-medium text-purple-600">Gogi:</span>{' '}
                                {conversation.bot_messages.length > 100 
                                  ? conversation.bot_messages.substring(0, 100) + '...'
                                  : conversation.bot_messages
                                }
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2 md:mb-0">
                          <Clock className="w-3 h-3" />
                          <span className="hidden sm:inline">Başlangıç: </span>
                          <span>{formatDate(conversation.first_message)}</span>
                        </div>
                        <div className="sm:hidden text-xs text-gray-500 mb-2">
                          Son mesaj: {formatDate(conversation.last_message)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadConversationDetail(conversation.session_id)}
                          className="flex items-center space-x-1 text-xs md:text-sm flex-1 sm:flex-initial"
                        >
                          <Eye className="w-3 h-3 md:w-4 md:h-4" />
                          <span>Detay</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteConversation(conversation.session_id)}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-xs md:text-sm flex-1 sm:flex-initial"
                        >
                          <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                          <span>Sil</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredConversations.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm md:text-base">Henüz konuşma bulunmuyor</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-4 md:mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                size="sm"
                className="text-xs md:text-sm"
              >
                <ChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
              </Button>
              <span className="text-xs md:text-sm text-gray-600 px-2">
                Sayfa {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                size="sm"
                className="text-xs md:text-sm"
              >
                <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Toast container otomatik oluşturulacak (simpleToast.js) */}
    </div>
  );
};

export default AdminGogiConversations;
